/**
 * Column registry for the unified Indexation grid. Pure data + pure logic (no
 * React, no date-fns) so it unit-tests on its own; the grid imports it and the
 * clicksGridEngine for the sort state.
 *
 * Indexation is the backbone, DMCA is a columns-extension (the design doc
 * "Единая страница"). Money columns come from the nightly-materialized m_*
 * fields — exact per grain, never rolled up.
 */
import type { IndexSortCol } from "@/services/IndexationService";
import type { IndexTargetRow } from "@/types/indexation";

export type IndexGrain = "domain" | "url";

// Lumen notice permalink. Duplicated here on purpose — types/dmca (where the
// original lives) is another session's zone, so this page does not import it.
export const LUMEN_NOTICE_URL = (id: string): string =>
  `https://lumendatabase.org/notices/${id}`;

// How the grid renders a column's cell. Kept as a tag the grid switches on, so
// the registry stays free of JSX.
export type IndexColFormat =
  | "target"
  | "brand"
  | "index_status"
  | "dmca_status"
  | "tier"
  | "vip"
  | "int"
  | "currency"
  | "notices"
  | "lumen"
  | "date"
  | "url_removed";

export interface IndexColumnDef {
  id: string;
  /** Header label. The pinned target column is relabelled Domain/URL by grain. */
  label: string;
  format: IndexColFormat;
  /** Server sort column id; absent = the column cannot be sorted. */
  sort?: IndexSortCol;
  align?: "right";
  /** The identity column: first, frozen left, always on, not in the picker. */
  pinned?: boolean;
}

// Registry order is the default on-screen order (drag reorders from here).
export const INDEX_COLUMNS: IndexColumnDef[] = [
  { id: "target", label: "Domain", format: "target", sort: "target", pinned: true },
  { id: "brand", label: "Brand", format: "brand", sort: "brand" },
  { id: "index_status", label: "Index status", format: "index_status", sort: "index_status" },
  { id: "dmca_status", label: "DMCA status", format: "dmca_status", sort: "dmca_status" },
  { id: "tier", label: "Tier", format: "tier", sort: "tier" },
  // VIP = a pinned re-check override (dmca_overrides). A client-side flag from the
  // active-override id set, so it is not server-sortable. See the VIP-only filter.
  { id: "vip", label: "VIP", format: "vip" },
  { id: "clicks", label: "Clicks", format: "int", sort: "clicks", align: "right" },
  { id: "revenue", label: "Revenue", format: "currency", sort: "revenue", align: "right" },
  { id: "conversions", label: "Conversions", format: "int", sort: "conversions", align: "right" },
  // Labels match the Clicks metric registry (src/config/metrics.ts) so the same
  // underlying field reads the same name everywhere: m_leads = "Leads",
  // m_sales = "Sales". The ids/sort keys stay (registrations/deposits/money) to
  // keep saved column sets and the sort enum stable — only the display changes.
  { id: "registrations", label: "Leads", format: "int", sort: "registrations", align: "right" },
  { id: "deposits", label: "Sales", format: "int", sort: "deposits", align: "right" },
  { id: "money", label: "Profit", format: "currency", sort: "money", align: "right" },
  { id: "notices", label: "Notices", format: "notices", sort: "notices", align: "right" },
  { id: "lumen", label: "Lumen", format: "lumen", align: "right" },
  { id: "dropped_since", label: "Dropped since", format: "date", sort: "dropped_since" },
  { id: "flagged_since", label: "Flagged since", format: "date", sort: "flagged_since" },
  { id: "url_removed", label: "URL removed", format: "url_removed" },
  { id: "last_checked", label: "Last checked", format: "date", sort: "last_checked" },
];

export const INDEX_COLUMN_BY_ID: ReadonlyMap<string, IndexColumnDef> = new Map(
  INDEX_COLUMNS.map((c) => [c.id, c]),
);

// Default visible set. Identity + index + headline money everywhere; the DMCA
// detail (Notices, Flagged since) is on by default on the url grain, where DMCA
// is most meaningful, and off on the domain grain.
const DEFAULT_DOMAIN = new Set([
  "target",
  "index_status",
  "dmca_status",
  "tier",
  "vip",
  "clicks",
  "revenue",
  "dropped_since",
  "last_checked",
]);
const DEFAULT_URL = new Set([...DEFAULT_DOMAIN, "notices", "flagged_since"]);

export const isDefaultVisible = (id: string, grain: IndexGrain): boolean =>
  (grain === "url" ? DEFAULT_URL : DEFAULT_DOMAIN).has(id);

/** The default visible column ids for a grain, in registry order. */
export const defaultColumns = (grain: IndexGrain): string[] =>
  INDEX_COLUMNS.filter((c) => c.pinned || isDefaultVisible(c.id, grain)).map((c) => c.id);

/**
 * Row highlight from the DMCA × index matrix (design doc "Матрица кейсов").
 * red/alarm — dropped out of the index on a money or DMCA target (cells 2/5/8);
 * amber/warn — a DMCA notice but still indexed (4/7), plus an unchecked money T1
 * target. A single transition is not enough to act on, so this is a signpost,
 * not a verdict (no history in the row to debounce flapping).
 */
export type AlertLevel = "alarm" | "warn" | null;

export function rowAlertLevel(
  row: Pick<IndexTargetRow, "index_status" | "last_status" | "tier" | "m_revenue" | "m_clicks">,
): AlertLevel {
  const dropped = row.index_status === "not_indexed";
  const unchecked = row.index_status == null;
  const dmca = row.last_status === "dmca";
  const money = (row.m_revenue ?? 0) > 0 || (row.m_clicks ?? 0) > 0;
  if (dropped) {
    if (dmca && money) return "alarm"; // cells 5, 8 — takedown + gone, with money
    if (!dmca && row.tier === 1) return "alarm"; // cell 2 — gone for no reason, top money
  }
  if (dmca && !dropped) return "warn"; // cells 4, 7 — notice filed, page still ranking
  if (unchecked && row.tier === 1 && money) return "warn"; // a money T1 we haven't checked
  return null;
}
